/**
 * Sección **Análisis** de *Control de Precios*.
 *
 * El panel anterior no tenía gráficos en esta pantalla: era una lista para
 * bajar y bajar. Aquí se resume lo mismo en cuatro vistas, siempre con los
 * precios que hay en el filtro (ubicación, fecha, búsqueda y categoría):
 *
 * 1. Precio mínimo, promedio y máximo por producto.
 * 2. Cambios detectados por estación (cuánto subió o bajó cada una).
 * 3. Cuántas estaciones hay en cada rango de precio del producto con más datos.
 * 4. Peso de cada categoría en las estaciones consultadas.
 */

import { BarChart3 } from 'lucide-react';
import { useMemo } from 'react';

import { BarChart } from '@/components/charts/bar-chart';
import { DonutChart } from '@/components/charts/donut-chart';
import { Card, CardContent } from '@/components/ui/card';

import type { CambioPricing, ComparacionPricing, PrecioPricing } from '../pricing.types';
import { histograma, resumenDePrecios, topPor } from '../pricing-utils';

interface PreciosAnalisisProps {
  /** Precios del filtro actual (ubicación, fecha, búsqueda y categoría). */
  precios: PrecioPricing[];
  /** Comparación de los dos últimos ficheros, si está disponible. */
  comparacion?: ComparacionPricing;
}

export function PreciosAnalisis({ precios, comparacion }: PreciosAnalisisProps) {
  /** Precio mínimo, promedio y máximo de cada producto. */
  const porProducto = useMemo(() => {
    const grupos = new Map<string, number[]>();

    for (const fila of precios) {
      const lista = grupos.get(fila.producto) ?? [];
      lista.push(Number(fila.precio) || 0);
      grupos.set(fila.producto, lista);
    }

    const detalle = [...grupos.entries()].map(([producto, valores]) => ({
      producto,
      ...resumenDePrecios(valores),
    }));

    return {
      detalle,
      top: topPor(detalle, 8, (item) => item.cantidad),
    };
  }, [precios]);

  /** Cambios por estación, separando subidas y bajadas. */
  const porEstacion = useMemo(() => {
    const grupos = new Map<string, { subio: number; bajo: number }>();

    const acumular = (cambios: CambioPricing[], signo: number) => {
      for (const cambio of cambios) {
        const actual = grupos.get(cambio.estacion) ?? { subio: 0, bajo: 0 };

        if (signo > 0) actual.subio += Math.abs(cambio.diferencia ?? 0);
        else actual.bajo += Math.abs(cambio.diferencia ?? 0);

        grupos.set(cambio.estacion, actual);
      }
    };

    acumular(comparacion?.aumentos ?? [], 1);
    acumular(comparacion?.bajadas ?? [], -1);

    const detalle = [...grupos.entries()].map(([estacion, valores]) => ({
      estacion,
      subio: Number(valores.subio.toFixed(2)),
      bajo: -Number(valores.bajo.toFixed(2)),
    }));

    return topPor(detalle, 8, (item) => item.subio - item.bajo);
  }, [comparacion]);

  /** Histograma del producto con más estaciones del filtro. */
  const distribucion = useMemo(() => {
    const producto = porProducto.top[0]?.producto;

    if (!producto) return { producto: '', etiquetas: [] as string[], valores: [] as number[] };

    const valores = precios
      .filter((fila) => fila.producto === producto)
      .map((fila) => Number(fila.precio) || 0);

    return { producto, ...histograma(valores) };
  }, [precios, porProducto.top]);

  /** Estaciones por categoría. */
  const porCategoria = useMemo(() => {
    const grupos = new Map<string, number>();

    for (const fila of precios) {
      grupos.set(fila.categoria, (grupos.get(fila.categoria) ?? 0) + 1);
    }

    return [...grupos.entries()];
  }, [precios]);

  const sinDatos = precios.length === 0;

  return (
    <div className="space-y-4">
      {sinDatos ? (
        <Card>
          <CardContent>
            <p className="text-sm text-ink-500">
              No hay precios con los filtros actuales, así que no hay nada que analizar.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="space-y-2">
              <header className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <BarChart3 className="size-4 text-brand-500" aria-hidden="true" />
                  Precio por producto
                </h2>
                <span className="text-xs text-ink-400">
                  mínimo, promedio y máximo de las {porProducto.top.length} familias con más
                  estaciones
                </span>
              </header>
              <BarChart
                labels={porProducto.top.map((item) => item.producto)}
                format="currency"
                height={260}
                series={[
                  { label: 'Mínimo', data: porProducto.top.map((item) => item.minimo) },
                  { label: 'Promedio', data: porProducto.top.map((item) => item.promedio) },
                  { label: 'Máximo', data: porProducto.top.map((item) => item.maximo) },
                ]}
              />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-2">
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-ink-900">Cambios por estación</h2>
                  <span className="text-xs text-ink-400">
                    suma de las diferencias entre los dos últimos ficheros
                  </span>
                </header>
                {porEstacion.length === 0 ? (
                  <p className="text-sm text-ink-500">
                    Sin cambios que mostrar: el detector todavía no comparó dos ficheros.
                  </p>
                ) : (
                  <BarChart
                    labels={porEstacion.map((item) => item.estacion)}
                    format="currency"
                    height={240}
                    series={[
                      { label: 'Subió', data: porEstacion.map((item) => item.subio) },
                      { label: 'Bajó', data: porEstacion.map((item) => item.bajo) },
                    ]}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2">
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-ink-900">Estaciones por precio</h2>
                  <span className="text-xs text-ink-400">
                    {distribucion.producto || 'sin producto'}
                  </span>
                </header>
                {distribucion.etiquetas.length === 0 ? (
                  <p className="text-sm text-ink-500">No hay precios para repartir en rangos.</p>
                ) : (
                  <BarChart
                    labels={distribucion.etiquetas}
                    height={240}
                    series={[{ label: 'Estaciones', data: distribucion.valores }]}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="space-y-2">
              <header className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink-900">Estaciones por categoría</h2>
                <span className="text-xs text-ink-400">{precios.length} estaciones con precio</span>
              </header>
              <DonutChart
                labels={porCategoria.map(([categoria]) => categoria)}
                series={[
                  { label: 'Estaciones', data: porCategoria.map(([, cantidad]) => cantidad) },
                ]}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2">
              <h2 className="text-sm font-semibold text-ink-900">Detalle por producto</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                      <th className="py-2">Producto</th>
                      <th className="py-2 text-right">Estaciones</th>
                      <th className="py-2 text-right">Mínimo</th>
                      <th className="py-2 text-right">Promedio</th>
                      <th className="py-2 text-right">Máximo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porProducto.detalle
                      .sort((a, b) => a.promedio - b.promedio)
                      .map((item) => (
                        <tr key={item.producto} className="border-b border-ink-50 last:border-0">
                          <td className="py-2 text-ink-700">{item.producto}</td>
                          <td className="py-2 text-right text-ink-500">{item.cantidad}</td>
                          <td className="py-2 text-right text-ink-500">
                            {item.minimo.toFixed(2)}
                          </td>
                          <td className="py-2 text-right font-medium text-ink-900">
                            {item.promedio.toFixed(2)}
                          </td>
                          <td className="py-2 text-right text-ink-500">
                            {item.maximo.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
